import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { FONT_FAMILY } from '../../../constants/styles';
import {
  selectBMIMeasurementEntity,
  selectBMIMeasurementModalEntity,
  selectDistanceMeasurementEntity,
  selectDistanceMeasurementModalEntity,
  selectHeartRateMeasurementEntity,
  selectHeartRateMeasurementModalHistoricList,
  selectHeightMeasurementEntity,
  selectHeightMeasurementModalEntity,
  selectSaturationMeasurementEntity,
  selectSaturationMeasurementModalHistoricList,
  selectStepsMeasurementEntity,
  selectStepsMeasurementModalEntity,
  selectWeightMeasurementEntity,
  selectWeightMeasurementModalEntity,
  selectBloodGlucoseMeasurementEntity,
  selectBloodGlucoseMeasurementModalEntity,
} from '../../../redux/profiles/patientHealthboard/selectors';
import {
  CHART_FILTERS_TYPE,
  DATA_SOURCE_TYPE,
  VITALS_ACTIVITY_TYPE,
} from '../constants';
import {
  adaptResponse,
  chooseAvailableSourceData,
  getDataListByType,
  getTooltipDate,
  makeDateForAverageOrTotal,
  makeTickForData,
  makeValueForGraf,
  shouldHideAverageLine,
  sourceDataCodeFilteredByDate,
} from '../utils';
import { useCalculatePaddingYAxis } from '../TimeScalableChart/useCalculatePaddingYAxis';
import { useSafeDataSourceTitle } from '../../common/Charts/hooks';

const {
  SATURATION,
  WEIGHT,
  BMI,
  DISTANCE,
  STEPS,
  HEIGHT,
  HEART_RATE,
  BLOOD_GLUCOSE,
} = VITALS_ACTIVITY_TYPE;
const { MONTH, WEEK, QUARTER, DAY, YEAR } = CHART_FILTERS_TYPE;

export function useDataGraf({
  formatMessage,
  dataType,
  filterType,
  chartInstanceRef,
  singleDayMode,
  isWeightInKg,
  isMilligramsPerDeciliter,
}) {
  const isGrafTypeLine = [WEIGHT, BMI, HEIGHT].includes(dataType);
  const isCandleDataType = [SATURATION, HEART_RATE, BLOOD_GLUCOSE].includes(
    dataType,
  );
  const isWithoutLimit = [HEART_RATE, BLOOD_GLUCOSE].includes(dataType);
  const { getSafeDataSourceTitle } = useSafeDataSourceTitle();

  const stepsData = chooseAvailableSourceData(
    adaptResponse(useSelector(selectStepsMeasurementEntity)),
  );
  const stepsDataModal = chooseAvailableSourceData(
    adaptResponse(useSelector(selectStepsMeasurementModalEntity)),
  );
  const distanceData = chooseAvailableSourceData(
    adaptResponse(useSelector(selectDistanceMeasurementEntity)),
  );
  const distanceDataModal = chooseAvailableSourceData(
    adaptResponse(useSelector(selectDistanceMeasurementModalEntity)),
  );
  const bmiData = chooseAvailableSourceData(
    adaptResponse(useSelector(selectBMIMeasurementEntity)),
  );
  const bmiDataModal = chooseAvailableSourceData(
    adaptResponse(useSelector(selectBMIMeasurementModalEntity)),
  );
  const weightData = chooseAvailableSourceData(
    adaptResponse(useSelector(selectWeightMeasurementEntity)),
  );
  const weightDataModal = chooseAvailableSourceData(
    adaptResponse(useSelector(selectWeightMeasurementModalEntity)),
  );
  const heightData = chooseAvailableSourceData(
    adaptResponse(useSelector(selectHeightMeasurementEntity)),
  );
  const heightDataModal = chooseAvailableSourceData(
    adaptResponse(useSelector(selectHeightMeasurementModalEntity)),
  );
  const saturationData = chooseAvailableSourceData(
    adaptResponse(useSelector(selectSaturationMeasurementEntity)),
  );
  const saturationDataModal = chooseAvailableSourceData(
    adaptResponse(useSelector(selectSaturationMeasurementModalHistoricList)),
  );
  const heartRateData = chooseAvailableSourceData(
    adaptResponse(useSelector(selectHeartRateMeasurementEntity)),
  );
  const heartRateDataModal = chooseAvailableSourceData(
    adaptResponse(useSelector(selectHeartRateMeasurementModalHistoricList)),
  );
  const bloodGlucoseData = chooseAvailableSourceData(
    adaptResponse(useSelector(selectBloodGlucoseMeasurementEntity)),
  );
  const bloodGlucoseDataModal = chooseAvailableSourceData(
    adaptResponse(useSelector(selectBloodGlucoseMeasurementModalEntity)),
  );

  const getEntityData = useMemo(
    () => () => {
      switch (dataType) {
        case STEPS:
          return singleDayMode ? stepsDataModal : stepsData;
        case DISTANCE:
          return singleDayMode ? distanceDataModal : distanceData;
        case BMI:
          return singleDayMode ? bmiDataModal : bmiData;
        case WEIGHT:
          return singleDayMode ? weightDataModal : weightData;
        case HEIGHT:
          return singleDayMode ? heightDataModal : heightData;
        case SATURATION:
          return singleDayMode ? saturationDataModal : saturationData;
        case HEART_RATE:
          return singleDayMode ? heartRateDataModal : heartRateData;
        case BLOOD_GLUCOSE:
          return singleDayMode ? bloodGlucoseDataModal : bloodGlucoseData;
        default:
          return {};
      }
    },
    [
      dataType,
      stepsData,
      distanceData,
      bmiData,
      weightData,
      heightData,
      filterType,
      singleDayMode,
      stepsDataModal,
      distanceDataModal,
      bmiDataModal,
      weightDataModal,
      heightDataModal,
      saturationDataModal,
      saturationData,
      heartRateDataModal,
      heartRateData,
      bloodGlucoseData,
      bloodGlucoseDataModal,
    ],
  );

  const getOptionsWithFilterType = useMemo(
    () => () => {
      switch (filterType) {
        case MONTH.type:
          return {
            maxBarThickness: 14,
          };
        case DAY.type:
          return {
            maxBarThickness: 14,
          };
        case WEEK.type:
          return {
            maxBarThickness: 29,
          };
        case QUARTER.type:
          return {
            maxBarThickness: 29,
          };
        case YEAR.type:
          return {
            maxBarThickness: 29,
          };
      }
    },
    [filterType, singleDayMode],
  );
  const adaptEntityForGraf = useMemo(
    () => () => {
      const rowData = getEntityData();
      const checkForAvailableData =
        rowData && rowData.measurements && rowData.measurements.length !== 0;
      return {
        labels: checkForAvailableData
          ? makeValueForGraf(
              rowData.measurements,
              'labels',
              filterType,
              false,
              dataType,
              singleDayMode,
            )
          : [],
        datasets: [
          {
            data: checkForAvailableData
              ? makeValueForGraf(
                  rowData.measurements,
                  'value',
                  filterType,
                  false,
                  dataType,
                  singleDayMode,
                )
              : [],
            backgroundColor: checkForAvailableData
              ? makeValueForGraf(
                  rowData.measurements,
                  'background',
                  filterType,
                  false,
                  dataType,
                  singleDayMode,
                )
              : [],
            barPercentage: 0.5,
            yAxisID: 'right-y-axis',
            ...(isGrafTypeLine && {
              lineTension: 0.4,
              borderColor: '#000000',
              borderWidth: 1,
              spanGaps: true,
              pointBackgroundColor: '#000000',
              pointBorderColor: '#000000',
              pointBorderWidth: 1,
              pointHoverBackgroundColor: '#ffffff',
              pointHoverBorderColor: '#000000',
              pointHoverBorderWidth: 2,
              pointHoverRadius: 6,
              pointRadius: checkForAvailableData
                ? makeValueForGraf(
                    rowData.measurements,
                    'points',
                    filterType,
                    false,
                    dataType,
                    singleDayMode,
                  )
                : [],
            }),
            sourceType: {
              code: DATA_SOURCE_TYPE.DEVICES,
              codes:
                checkForAvailableData &&
                rowData.measurements.map(el => {
                  return {
                    providerCode: el.originalProviderCodes,
                    value: el.value,
                    measuredAt: el.measuredAt,
                  };
                }),
            },
            ...getOptionsWithFilterType(),
          },
        ],
        timeSets: checkForAvailableData
          ? makeValueForGraf(
              rowData.measurements,
              'value',
              filterType,
              true,
              dataType,
              singleDayMode,
            )
          : [],
        optionsToolTip: checkForAvailableData
          ? makeValueForGraf(
              rowData.measurements,
              'tooltip',
              filterType,
              false,
              dataType,
              singleDayMode,
            )
          : [],
        averageOrTotal: {
          dateForSingleMode: checkForAvailableData
            ? makeDateForAverageOrTotal(rowData.measurements, filterType, true)
            : '',
          value: checkForAvailableData
            ? makeValueForGraf(
                rowData.measurements,
                'value',
                filterType,
                false,
                dataType,
                singleDayMode,
                true,
              )
            : '',
          showAverageLine: checkForAvailableData
            ? shouldHideAverageLine(rowData.measurements, isCandleDataType)
            : false,
        },
        ...(isCandleDataType && { isCandleType: true }),
      };
    },
    [getEntityData, filterType, dataType, singleDayMode, isCandleDataType],
  );

  const calculatePaddingForYAxis = useCalculatePaddingYAxis();

  const getGlobalOptionsForChart = useMemo(
    () => (type, isWeightMetricInKg) => {
      return {
        scales: {
          xAxes: [
            {
              offset: true,
              gridLines: {
                borderDash: [2, 4],
                color: '#e5e5e5',
                display: false,
                drawOnChartArea: false,
                drawTicks: true,
              },
              ticks: {
                padding: 0,
                fontColor: 'rgba(84, 118, 164, 0.6)',
                fontSize: 12,
                fontFamily: 'Helvetica',
                fontStyle: 'bold',
                autoSkip: true,
                autoSkipPadding: singleDayMode ? 10 : 15,
                maxRotation: 0,
              },
              ...(isCandleDataType && { stacked: true }),
            },
          ],
          yAxes: [
            {
              id: 'right-y-axis',
              type: 'linear',
              position: 'right',
              offset: true,
              gridLines: {
                borderDash: [2, 4],
                color: '#e5e5e5',
                drawOnChartArea: true,
                drawTicks: true,
                drawBorder: false,
                zeroLineWidth: 0,
                zeroLineColor: '#ffffff',
              },
              ticks: {
                padding: 1,
                beginAtZero: false,
                fontColor: 'rgba(38, 65, 74, 0.4)',
                fontSize: 11,
                fontFamily: 'Helvetica',
                callback(value) {
                  return makeTickForData(value, type, true, isWeightMetricInKg);
                },
                ...(isCandleDataType && {
                  min: 75,
                  max: 100,
                  autoSkip: true,
                  autoSkipPadding: calculatePaddingForYAxis,
                  callback(value, _, data) {
                    if (dataType === BLOOD_GLUCOSE && !isMilligramsPerDeciliter)
                      return value === 0 ? 0 : value.toFixed(1);
                    const roundedValue = Math.round(value);
                    if (data.includes(roundedValue) && roundedValue !== value)
                      return null;
                    return value > 100 && !isWithoutLimit ? null : roundedValue;
                  },
                }),
              },
              ...(isCandleDataType && { stacked: true }),
            },
          ],
        },
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 30,
            bottom: 0,
          },
        },
        legend: {
          display: false,
        },
        cornerRadius: 4,
        fullCornerRadius: false,
        stackedRounded: false,
        animation: {
          duration: 0,
        },
        tooltips: {
          backgroundColor: '#ffffff',
          displayColors: false,
          titleFontColor: '#26414a',
          titleFontSize: 16,
          titleFontStyle: 'bold',
          titleFontFamily: FONT_FAMILY.primary,
          footerFontSize: 11,
          footerFontStyle: 'normal',
          footerFontColor: 'rgba(38, 65, 74, 0.75)',
          bodyFontSize: 11,
          footerSpacing: 6,
          footerMarginTop: 6,
          bodyFontColor: 'rgba(38, 65, 74, 0.75)',
          xPadding: 9,
          yPadding: 9,
          borderColor: 'rgba(39, 45, 57, 0.05)',
          borderWidth: 1,
          shadowOffsetX: 3,
          shadowOffsetY: 3,
          shadowBlur: 10,
          shadowColor: 'rgba(39, 45, 57, 0.15)',
          callbacks: {
            title(tooltipItem, data) {
              if (!tooltipItem.length) return;
              const { value, metric } = getDataListByType({
                formatMessage,
                type: dataType,
                item: {
                  value: isCandleDataType
                    ? data.datasets[tooltipItem[0].datasetIndex].ranges[
                        tooltipItem[0].index
                      ]
                    : tooltipItem[0].value,
                  measuredAt: '',
                },
                isWeightInKg: isWeightMetricInKg,
                isMilligramsPerDeciliter,
                isConvertedData: true,
              });
              return `${value} ${metric}`;
            },
            afterFooter(item, data) {
              if (!item.length) return;
              if (isCandleDataType || isGrafTypeLine) {
                const sourceType =
                  data.datasets[item[0].datasetIndex].sourceType.code;
                if (
                  [QUARTER.type, YEAR.type].includes(filterType) &&
                  isGrafTypeLine &&
                  sourceType === DATA_SOURCE_TYPE.CCD
                ) {
                  return formatMessage({
                    id: 'patient.healthboard.clickSeeAllData',
                  });
                }
              }
            },
            label(tooltipItem, data) {
              if (!tooltipItem) return;
              const sourceData =
                data.datasets[tooltipItem.datasetIndex].sourceType.code;
              return getTooltipDate(
                sourceData,
                data,
                tooltipItem,
                isCandleDataType,
                isGrafTypeLine,
                filterType,
              );
            },
            footer(tooltipItem, data) {
              if (!tooltipItem.length) return;
              const tooltip = tooltipItem[0];
              const sourceData =
                data.datasets[tooltip.datasetIndex].sourceType.title;
              const sourceTypeDataCode =
                data.datasets[tooltip.datasetIndex].sourceType.code;
              const sourceDataCodes =
                data.datasets[tooltip.datasetIndex].sourceType.codes;
              if (sourceDataCodes) {
                const tooltipDate = getTooltipDate(
                  sourceTypeDataCode,
                  data,
                  tooltip,
                  isCandleDataType,
                  isGrafTypeLine,
                  filterType,
                );
                const sourceCode = sourceDataCodeFilteredByDate(
                  sourceDataCodes,
                  filterType,
                  tooltipDate,
                  formatMessage,
                  getSafeDataSourceTitle,
                );
                if (sourceCode) {
                  return sourceCode;
                }
                return sourceData || '';
              }

              return sourceData || '';
            },
          },
        },
        onClick: (evt, element) => {
          if (element.length > 0) {
            const allEntity =
              chartInstanceRef.current.chartInstance.data.optionsToolTip;
            if (isCandleDataType) {
              const indexActiveDataset =
                element[0]._chart.tooltip._active[0]._datasetIndex;
              allEntity[indexActiveDataset][0].forEach((item, index) => {
                if (index === element[0]._index && item.keepTooltipOpen) {
                  // eslint-disable-next-line no-param-reassign
                  item.keepTooltipOpen = false;
                  // eslint-disable-next-line no-param-reassign
                } else item.keepTooltipOpen = index === element[0]._index;
              });
            } else {
              // eslint-disable-next-line no-param-reassign,no-return-assign
              allEntity.forEach((item, index) => {
                if (index === element[0]._index && item.keepTooltipOpen) {
                  // eslint-disable-next-line no-param-reassign
                  item.keepTooltipOpen = false;
                  // eslint-disable-next-line no-param-reassign
                } else item.keepTooltipOpen = index === element[0]._index;
              });
            }
          }
        },
      };
    },
    [
      dataType,
      singleDayMode,
      isWeightInKg,
      isCandleDataType,
      filterType,
      isWithoutLimit,
      isMilligramsPerDeciliter,
    ],
  );

  return {
    data: adaptEntityForGraf(),
    options: getGlobalOptionsForChart(dataType, isWeightInKg),
    isGrafTypeLine,
  };
}



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/Chart/useDataGraf.jsx